import React, { useMemo, useState } from 'react';

import { Autocomplete, Box, Checkbox, Chip, FormControl, FormHelperText, InputLabel, ListItemText, ListSubheader, MenuItem, OutlinedInput, Select, TextField } from '@mui/material';
import { ErrorMessage, FastField, getIn } from 'formik';

const SelectField = ({ label, size, onChange, value, options, sx }) => {
    const randomId = Date.now().toString();

    return (
        <FormControl fullWidth sx={ sx } size={ size }>
            <InputLabel id={ randomId }>{ label }</InputLabel>
            <Select
                labelId={ randomId }
                value={ value }
                label={ label }
                onChange={ onChange }
            >
                {
                    options?.map(option => (
                        <MenuItem key={ option?.value } value={ option?.value }>{ option?.label }</MenuItem>
                    ))
                }
            </Select>
        </FormControl>
    );
};

export const SelectFieldMultiple = ({ label, size, options, value, onChange, onClose, sx }) => {
    const randomId = Date.now().toString();

    return (
        <FormControl fullWidth sx={ sx } size={ size }>
            <InputLabel id={ randomId }>{ label }</InputLabel>
            <Select
                labelId={ randomId }
                multiple
                value={ value }
                onClose={ onClose }
                onChange={ onChange }
                input={ <OutlinedInput label={ label }/> }
                renderValue={ selectedValues => (
                    <Box sx={ {
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: size === 'small' ? -0.5 : 0,
                        mb: size === 'small' ? -0.5 : 0
                    } }>
                        {
                            selectedValues.map(selectedValue => (
                                <Chip
                                    key={ selectedValue }
                                    size="small"
                                    label={ options?.find(option => option?.value === selectedValue)?.label || selectedValue }
                                />
                            ))
                        }
                    </Box>
                ) }
            >
                {
                    options?.map(option => (
                        <MenuItem key={ option?.value } value={ option?.value }>
                            <Checkbox checked={ value.find(v => v === option?.value) !== undefined }/>
                            <ListItemText primary={ option?.label }/>
                        </MenuItem>
                    ))
                }
            </Select>
        </FormControl>
    );
};

export const SelectFieldMultipleWithMore = ({
                                                label,
                                                size,
                                                defaultOptions = [],
                                                extraOptions = [],
                                                value = [],
                                                onChange,
                                                onClose,
                                                sx
                                            }) => {
    const randomId = Date.now().toString();
    const [showMore, setShowMore] = useState(false);
    const [filterText, setFilterText] = useState('');

    const allOptions = useMemo(() => [...defaultOptions, ...extraOptions], [defaultOptions, extraOptions]);
    const isExpanded = showMore || filterText.trim() !== '';

    const menuOptions = useMemo(() => {
        const optionsToShow = [...defaultOptions, ...(isExpanded ? extraOptions : [])];
        if (!filterText) return optionsToShow;
        const lower = filterText.toLowerCase();
        return optionsToShow.filter(opt => opt.label.toLowerCase().includes(lower));
    }, [defaultOptions, extraOptions, isExpanded, filterText]);

    return (
        <FormControl fullWidth sx={ sx } size={ size }>
            <InputLabel id={ randomId }>{ label }</InputLabel>
            <Select
                labelId={ randomId }
                multiple
                value={ value }
                onClose={ onClose }
                onChange={ onChange }
                input={ <OutlinedInput label={ label }/> }
                MenuProps={ { style: { maxHeight: 400 }, disableAutoFocusItem: true } }
                renderValue={ (selectedValues) => (
                    <Box sx={ {
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: size === 'small' ? -0.5 : 0,
                        mb: size === 'small' ? -0.5 : 0
                    } }>
                        { selectedValues.map(selectedValue => (
                            <Chip
                                key={ selectedValue }
                                size="small"
                                label={ allOptions.find(option => option.value === selectedValue)?.label || selectedValue }
                            />
                        )) }
                    </Box>
                ) }
            >
                <Box sx={ { px: 1, py: 0.5 } }>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Szukaj..."
                        value={ filterText }
                        onChange={ (e) => setFilterText(e.target.value) }
                        onKeyDown={ (e) => e.stopPropagation() }
                    />
                </Box>

                { defaultOptions.length > 0 && <ListSubheader>Podstawowe</ListSubheader> }
                { menuOptions.filter(opt => defaultOptions.includes(opt)).map(option => (
                    <MenuItem key={ option.value } value={ option.value }>
                        <Checkbox checked={ value.includes(option.value) }/>
                        <ListItemText primary={ option.label }/>
                    </MenuItem>
                )) }

                { isExpanded && extraOptions.length > 0 && <ListSubheader>Dodatkowe</ListSubheader> }
                { isExpanded && menuOptions.filter(opt => extraOptions.includes(opt)).map(option => (
                    <MenuItem key={ option.value } value={ option.value }>
                        <Checkbox checked={ value.includes(option.value) }/>
                        <ListItemText primary={ option.label }/>
                    </MenuItem>
                )) }

                { extraOptions.length > 0 && filterText.trim() === '' && (
                    <Box sx={ { textAlign: 'center', p: 1 } }>
                        <span
                            style={ { cursor: 'pointer', fontWeight: 500 } }
                            onClick={ (e) => {
                                e.stopPropagation();
                                setShowMore(!showMore);
                            } }
                        >
                          { showMore ? '➖ Pokaż mniej' : '➕ Pokaż więcej' }
                        </span>
                    </Box>
                ) }
            </Select>
        </FormControl>
    );
};

export const SelectFieldFormik = ({ name, size, label, options, disabled, required, onChange, sx }) => {
    const randomId = Date.now().toString();

    return (
        <FastField
            name={ name }
            shouldUpdate={ (nextProps, currentProps) => {
                if (getIn(nextProps.formik.values, name) !== getIn(currentProps.formik.values, name)) return true;
                if (getIn(nextProps.formik.errors, name) !== getIn(currentProps.formik.errors, name)) return true;
                if (getIn(nextProps.formik.touched, name) !== getIn(currentProps.formik.touched, name)) return true;
                if (nextProps.disabled !== currentProps.disabled) return true;
                if (nextProps.required !== currentProps.required) return true;
                if (nextProps.options?.length !== currentProps.options?.length) return true;

                return false;
            } }
        >
            { ({ form: { errors, setFieldValue }, field: { value } }) => (
                <FormControl fullWidth disabled={ disabled } required={ required } sx={ sx } size={ size }>
                    <InputLabel id={ randomId }>{ label }</InputLabel>
                    <Select
                        labelId={ randomId }
                        error={ !!getIn(errors, name) }
                        value={ value }
                        label={ label }
                        onChange={ (event) => {
                            setFieldValue(name, event.target.value);
                            onChange && onChange(event.target.value);
                        } }
                        MenuProps={ {
                            style: { maxHeight: 400 },
                        } }
                    >
                        {
                            options?.map(option => (
                                <MenuItem key={ option?.value } value={ option?.value }>{ option?.label }</MenuItem>
                            ))
                        }
                    </Select>
                    <FormHelperText error>
                        <ErrorMessage name={ name }/>
                    </FormHelperText>
                </FormControl>
            ) }
        </FastField>
    );
};

export const SelectFieldFormikWithMore = ({
                                              name,
                                              label,
                                              size,
                                              defaultOptions = [],
                                              extraOptions = [],
                                              onChange,
                                              sx
                                          }) => {
    const [showMore, setShowMore] = useState(false);

    const options = useMemo(() => {
        if (showMore) return [...defaultOptions, ...extraOptions];
        return defaultOptions;
    }, [defaultOptions, extraOptions, showMore]);

    return (
        <FastField
            name={ name }
            shouldUpdate={ (nextProps, currentProps) => {
                if (getIn(nextProps.formik.values, name) !== getIn(currentProps.formik.values, name)) return true;
                if (getIn(nextProps.formik.errors, name) !== getIn(currentProps.formik.errors, name)) return true;
                if (getIn(nextProps.formik.touched, name) !== getIn(currentProps.formik.touched, name)) return true;
                if (nextProps.defaultOptions?.length !== currentProps.defaultOptions?.length) return true;
                if (nextProps.extraOptions?.length !== currentProps.extraOptions?.length) return true;

                return false;
            } }
        >
            { ({ field, form: { setFieldValue } }) => (
                <Autocomplete
                    options={ [...defaultOptions, ...(showMore ? extraOptions : [])] }
                    sx={ sx }
                    size={ size }
                    groupBy={ (option) => {
                        if (defaultOptions.includes(option)) return 'Podstawowe';
                        if (extraOptions.includes(option)) return 'Dodatkowe';
                        return '';
                    } }
                    getOptionLabel={ (option) => option.label }
                    value={ options.find(opt => opt.value === field.value) || null }
                    onChange={ (event, newValue) => {
                        setFieldValue(name, newValue?.value || '');
                        onChange && onChange(newValue?.value || '');
                    } }
                    renderInput={ (params) => <TextField { ...params } label={ label }/> }
                    renderGroup={ (params) => (
                        <>
                            <ListSubheader>{ params.group }</ListSubheader>
                            { params.children }
                            { params.group === 'Podstawowe' && extraOptions.length > 0 && (
                                <ListSubheader
                                    key="toggle-more"
                                    sx={ { cursor: 'pointer' } }
                                    onClick={ (e) => {
                                        e.stopPropagation();
                                        setShowMore(!showMore);
                                    } }
                                >
                                    { showMore ? '➖ Pokaż mniej' : '➕ Pokaż więcej' }
                                </ListSubheader>
                            ) }
                        </>
                    ) }
                    ListboxProps={ {
                        sx: { pt: 0, mt: 0 }
                    } }
                />
            ) }
        </FastField>
    );
};


export const SelectFieldMultipleFormik = ({ name, size, label, options, disabled, required, onChange, sx }) => {
    const randomId = Date.now().toString();

    return (
        <FastField
            name={ name }
            shouldUpdate={ (nextProps, currentProps) => {
                if (getIn(nextProps.formik.values, name) !== getIn(currentProps.formik.values, name)) return true;
                if (getIn(nextProps.formik.errors, name) !== getIn(currentProps.formik.errors, name)) return true;
                if (getIn(nextProps.formik.touched, name) !== getIn(currentProps.formik.touched, name)) return true;
                if (nextProps.disabled !== currentProps.disabled) return true;
                if (nextProps.required !== currentProps.required) return true;
                if (nextProps.options?.length !== currentProps.options?.length) return true;

                return false;
            } }
        >
            { ({ form: { errors, setFieldValue }, field: { value } }) => (
                <FormControl fullWidth disabled={ disabled } required={ required } sx={ sx } size={ size }>
                    <InputLabel id={ randomId }>{ label }</InputLabel>
                    <Select
                        labelId={ randomId }
                        multiple
                        error={ !!getIn(errors, name) }
                        value={ value }
                        onChange={ (event) => {
                            setFieldValue(name, event.target.value);
                            onChange && onChange(event.target.value);
                        } }
                        input={ <OutlinedInput label={ label }/> }
                        renderValue={ selectedValues => (
                            <Box sx={ {
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                mt: size === 'small' ? -0.5 : 0,
                                mb: size === 'small' ? -0.5 : 0
                            } }>
                                {
                                    selectedValues.map(selectedValue => (
                                        <Chip
                                            key={ selectedValue }
                                            size="small"
                                            label={ options?.find(option => option?.value === selectedValue)?.label || selectedValue }
                                        />
                                    ))
                                }
                            </Box>
                        ) }
                    >
                        {
                            options?.map(option => (
                                <MenuItem key={ option?.value } value={ option?.value }>
                                    <Checkbox checked={ value.find(v => v === option?.value) !== undefined }/>
                                    <ListItemText primary={ option?.label }/>
                                </MenuItem>
                            ))
                        }
                    </Select>
                    <FormHelperText error>
                        <ErrorMessage name={ name }/>
                    </FormHelperText>
                </FormControl>
            ) }
        </FastField>
    );
};

export const SelectFieldMultipleFormikWithMore = ({
                                                      name,
                                                      size,
                                                      label,
                                                      defaultOptions = [],
                                                      extraOptions = [],
                                                      disabled,
                                                      required,
                                                      onChange,
                                                      sx
                                                  }) => {
    const randomId = Date.now().toString();
    const [showMore, setShowMore] = useState(false);
    const [filterText, setFilterText] = useState('');

    const allOptions = useMemo(() => [...defaultOptions, ...extraOptions], [defaultOptions, extraOptions]);
    const isExpanded = showMore || filterText.trim() !== '';

    const menuOptions = useMemo(() => {
        const optionsToShow = [...defaultOptions, ...(isExpanded ? extraOptions : [])];
        if (!filterText) return optionsToShow;
        const lower = filterText.toLowerCase();
        return optionsToShow.filter(opt => opt.label.toLowerCase().includes(lower));
    }, [defaultOptions, extraOptions, isExpanded, filterText]);

    return (
        <FastField
            name={ name }
            shouldUpdate={ (nextProps, currentProps) => {
                if (getIn(nextProps.formik.values, name) !== getIn(currentProps.formik.values, name)) return true;
                if (getIn(nextProps.formik.errors, name) !== getIn(currentProps.formik.errors, name)) return true;
                if (getIn(nextProps.formik.touched, name) !== getIn(currentProps.formik.touched, name)) return true;
                if (nextProps.disabled !== currentProps.disabled) return true;
                if (nextProps.required !== currentProps.required) return true;
                if (nextProps.defaultOptions?.length !== currentProps.defaultOptions?.length) return true;
                if (nextProps.extraOptions?.length !== currentProps.extraOptions?.length) return true;

                return false;
            } }
        >
            { ({ form: { errors, setFieldValue }, field: { value } }) => (
                <FormControl fullWidth disabled={ disabled } required={ required } sx={ sx } size={ size }>
                    <InputLabel id={ randomId }>{ label }</InputLabel>
                    <Select
                        labelId={ randomId }
                        multiple
                        error={ !!getIn(errors, name) }
                        value={ value }
                        onChange={ (event) => {
                            setFieldValue(name, event.target.value);
                            onChange && onChange(event.target.value);
                        } }
                        input={ <OutlinedInput label={ label }/> }
                        MenuProps={ { style: { maxHeight: 400 }, disableAutoFocusItem: true } }
                        renderValue={ (selectedValues) => (
                            <Box sx={ {
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                mt: size === 'small' ? -0.5 : 0,
                                mb: size === 'small' ? -0.5 : 0
                            } }>
                                { selectedValues.map(selectedValue => (
                                    <Chip
                                        key={ selectedValue }
                                        size="small"
                                        label={ allOptions.find(option => option.value === selectedValue)?.label || selectedValue }
                                    />
                                )) }
                            </Box>
                        ) }
                    >
                        <Box sx={ { px: 1, py: 0.5 } }>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Szukaj..."
                                value={ filterText }
                                onChange={ (e) => setFilterText(e.target.value) }
                                onKeyDown={ (e) => e.stopPropagation() }
                            />
                        </Box>

                        { defaultOptions.length > 0 && <ListSubheader>Podstawowe</ListSubheader> }
                        { menuOptions.filter(opt => defaultOptions.includes(opt)).map(option => (
                            <MenuItem key={ option.value } value={ option.value }>
                                <Checkbox checked={ value.includes(option.value) }/>
                                <ListItemText primary={ option.label }/>
                            </MenuItem>
                        )) }

                        { isExpanded && extraOptions.length > 0 && <ListSubheader>Dodatkowe</ListSubheader> }
                        { isExpanded && menuOptions.filter(opt => extraOptions.includes(opt)).map(option => (
                            <MenuItem key={ option.value } value={ option.value }>
                                <Checkbox checked={ value.includes(option.value) }/>
                                <ListItemText primary={ option.label }/>
                            </MenuItem>
                        )) }

                        { extraOptions.length > 0 && filterText.trim() === '' && (
                            <Box sx={ { textAlign: 'center', p: 1 } }>
                                <span
                                    style={ { cursor: 'pointer', fontWeight: 500 } }
                                    onClick={ (e) => {
                                        e.stopPropagation();
                                        setShowMore(!showMore);
                                    } }
                                >
                                  { showMore ? '➖ Pokaż mniej' : '➕ Pokaż więcej' }
                                </span>
                            </Box>
                        ) }
                    </Select>
                    <FormHelperText error>
                        <ErrorMessage name={ name }/>
                    </FormHelperText>
                </FormControl>
            ) }
        </FastField>
    );
};

export default SelectField;